import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown, ChevronLeft, ChevronRight, Phone, BookOpen, Home, Image as ImageIcon, FileText, Play } from "lucide-react";
import { PhoneField } from "@/components/PhoneField";
import { LangProvider, useLang } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import heroImg from "@/assets/hero-tower.webp";
import mandarinHeroVideoAsset from "@/assets/mandarin-hero.mp4.asset.json";
import greatRoomAsset from "@/assets/great-room.webp.asset.json";
const interiorImg = greatRoomAsset.url;
import lagoonPoolAsset from "@/assets/lagoon-pool.jpg.asset.json";
const poolImg = lagoonPoolAsset.url;
import poolMandarinAsset from "@/assets/pool-mandarin.jpg.asset.json";
import logoAsset from "@/assets/logo.webp.asset.json";
import locationAsset from "@/assets/hotel-arrival.webp.asset.json";
import g1 from "@/assets/gallery-1.jpg.asset.json";
import g2 from "@/assets/gallery-2.jpg.asset.json";
import g3 from "@/assets/gallery-3.jpg.asset.json";
import g4 from "@/assets/gallery-4.jpg.asset.json";
import g5 from "@/assets/gallery-5.jpg.asset.json";
import g6 from "@/assets/gallery-6.jpg.asset.json";
import residenceCFloorPlanAsset from "@/assets/residence-c-floor-plan.webp.asset.json";
import residence02FloorPlanAsset from "@/assets/residence-02-floor-plan.webp.asset.json";
import residence6101FloorPlanAsset from "@/assets/residence-6101-floor-plan.webp.asset.json";
import residence6102FloorPlanAsset from "@/assets/residence-6102-floor-plan.webp.asset.json";
import g7 from "@/assets/gallery-7.jpg.asset.json";
import g8 from "@/assets/gallery-8.jpg.asset.json";
import residencesParallaxAsset from "@/assets/residences-parallax.webp.asset.json";
import luxuryIntlAsset from "@/assets/luxury-international-realty.png.asset.json";
import zagaTeamAsset from "@/assets/zaga-team.webp.asset.json";
import realtorMlsAsset from "@/assets/realtor-mls-eho.png.asset.json";
import { ParallaxHero } from "@/components/ParallaxHero";
import { Reveal } from "@/components/Reveal";
import northTowerTerraceAsset from "@/assets/north-tower-terrace.webp.asset.json";
import residence02VideoAsset from "@/assets/collection-i-residence-02.mp4.asset.json";
import residence02PosterAsset from "@/assets/collection-i-residence-02-poster.jpg.asset.json";
import collectionIIResidence03VideoAsset from "@/assets/collection-ii-residence-03.mp4.asset.json";
import collectionIIResidence03PosterAsset from "@/assets/collection-ii-residence-03-poster.jpg.asset.json";
import swireMakingNeighborhoodVideoAsset from "@/assets/swire-making-a-neighborhood.mp4.asset.json";
import swireMakingNeighborhoodPosterAsset from "@/assets/swire-making-a-neighborhood-poster.jpg.asset.json";
const northTowerImg = northTowerTerraceAsset.url;
import floorPlansAsset from "@/assets/floor-plans.pdf.asset.json";
import southFloorPlansAsset from "@/assets/south-tower-floor-plans.pdf.asset.json";
import brochureAsset from "@/assets/brochure.pdf.asset.json";
import factsheetAsset from "@/assets/factsheet.pdf.asset.json";

function Gallery() {
  const { t } = useLang();
  const imgs = [g1, g2, g3, g4, g5, g6, g7, g8, g1, g2, g3, g4];
  const [active, setActive] = useState<number | null>(null);

  useEffect(() => {
    if (active === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setActive(null);
      if (e.key === "ArrowLeft") setActive((prev) => (prev === null || prev === 0 ? imgs.length - 1 : prev - 1));
      if (e.key === "ArrowRight") setActive((prev) => (prev === null || prev === imgs.length - 1 ? 0 : (prev ?? 0) + 1));
    };
    window.addEventListener("keydown", onKey);
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [active, imgs.length]);

  return (
    <section id="gallery" className="bg-background pt-20 md:pt-28 pb-10 md:pb-14">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="eyebrow text-gold text-xs tracking-[0.3em] mb-3">{t("gallery.eyebrow")}</p>
          <h2 className="font-serif text-3xl md:text-5xl text-foreground">{t("gallery.title")}</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {imgs.map((im, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className="group relative aspect-[4/3] overflow-hidden text-left"
              aria-label={`Open image ${i + 1}`}
            >
              <img
                src={im.url}
                alt={`Mandarin Oriental Miami ${i + 1}`}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/20">
                <ImageIcon className="h-8 w-8 text-white opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </span>
            </button>
          ))}
        </div>
      </div>

      {active !== null &&
        createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 px-4 backdrop-blur-sm"
            onClick={() => setActive(null)}
          >
            <button
              aria-label="Close"
              onClick={() => setActive(null)}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>

            <button
              aria-label="Previous"
              onClick={(e) => {
                e.stopPropagation();
                setActive(active === 0 ? imgs.length - 1 : active - 1);
              }}
              className="absolute left-4 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 md:left-8"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>

            <button
              aria-label="Next"
              onClick={(e) => {
                e.stopPropagation();
                setActive(active === imgs.length - 1 ? 0 : active + 1);
              }}
              className="absolute right-4 top-1/2 z-10 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20 md:right-8"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            <img
              src={imgs[active].url}
              alt={`Mandarin Oriental Miami ${active + 1}`}
              className="max-h-[85vh] max-w-full object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
          </div>,
          document.body,
        )}
    </section>
  );
}

function CondosForSale() {
  const { t } = useLang();
  const { open: openModal } = useModal();
  const condos = [
    { title: "South Tower—Collection IV Residence 01", details: ["5 Bedrooms + Family Room", "6 Bathrooms", "Powder Room", "Indoor Area 5,840 ft² (543 m²)"], img: residenceCFloorPlanAsset.url },
    { title: "South Tower—Collection IV Residence 02", details: ["5 Bedrooms + Family Room", "6 Bathrooms", "Powder Room", "Indoor Area 5,840 ft² (543 m²)"], img: residence02FloorPlanAsset.url },
    { title: "South Tower—Collection IV Residence 6101", details: ["5 Bedrooms + Family Room", "6 Bathrooms", "Powder Room", "Indoor Area 5,681 ft² (528 m²)"], img: residence6101FloorPlanAsset.url },
    { title: "South Tower—Collection IV Residence 6102", details: ["5 Bedrooms + Family Room", "6 Bathrooms", "Powder Room", "Indoor Area 5,681 ft² (528 m²)"], img: residence6102FloorPlanAsset.url },
  ];
  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  const scrollToIndex = (i: number) => {
    const el = scroller.current;
    if (!el) return;
    const card = el.children[i] as HTMLElement | undefined;
    if (card) el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: "smooth" });
  };

  const onScroll = () => {
    const el = scroller.current;
    if (!el) return;
    const cardWidth = (el.children[0] as HTMLElement)?.offsetWidth || 1;
    const gap = 24;
    const idx = Math.round(el.scrollLeft / (cardWidth + gap));
    setActive(Math.min(condos.length - 1, Math.max(0, idx)));
  };

  return (
    <section id="condos" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        <h2 className="text-center font-serif text-3xl md:text-5xl text-foreground tracking-wide mb-10 md:mb-14">
          {t("condos.title")}
        </h2>
        <div className="relative -mx-6 md:mx-0">
          <div
            ref={scroller}
            onScroll={onScroll}
            className="flex gap-4 md:gap-6 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-2 px-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {condos.map((c, i) => (
              <article
                key={i}
                onClick={openModal}
                className="snap-center shrink-0 w-[80%] sm:w-[55%] md:w-[calc((100%-3rem)/3)] first:ml-[10%] last:mr-[10%] sm:first:ml-[22.5%] sm:last:mr-[22.5%] md:first:ml-0 md:last:mr-0 relative aspect-[16/10] overflow-hidden shadow-lg group transition-transform duration-500 ease-out cursor-pointer"
              >
                <img src={c.img} alt={c.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 text-white" style={{ textShadow: '0 2px 6px rgba(0,0,0,0.6)' }}>
                  <h3 className="font-serif text-lg md:text-2xl mb-1 leading-tight">{c.title}</h3>
                  <div className="text-xs md:text-sm opacity-90 space-y-0.5">
                    {c.details.map((line, idx) => (
                      <p key={idx}>{line}</p>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
          <div className="flex justify-center gap-2 mt-6">
            {condos.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => scrollToIndex(i)}
                className="flex h-11 w-11 items-center justify-center"
              >
                <span
                  className={cn(
                    "block h-2.5 w-2.5 rounded-full transition-colors",
                    active === i ? "bg-foreground" : "bg-foreground/30 hover:bg-foreground/50"
                  )}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "The Residences at Mandarin Oriental Miami — Luxury Bayfront Living" },
      {
        name: "description",
        content:
          "Iconic waterfront residences on Brickell Key. Two architectural towers, signature Mandarin Oriental service, and panoramic views of Biscayne Bay.",
      },
      { property: "og:title", content: "The Residences at Mandarin Oriental Miami — Luxury Bayfront Living" },
      {
        property: "og:description",
        content: "Iconic waterfront residences on Brickell Key. Two architectural towers, signature Mandarin Oriental service, and panoramic views of Biscayne Bay.",
      },
      { property: "og:image", content: heroImg },
    ],
  }),
  component: Landing,
});

const ModalCtx = createContext<{ open: () => void; openWithDownload: (downloadUrl: string) => void; openWithRedirect: (path: string) => void; notifyAgent: () => void; pendingDownload: string | null; pendingRedirect: string | null; clearPendingDownload: () => void; clearPendingRedirect: () => void; markSubmitted: () => void }>({ open: () => {}, openWithDownload: () => {}, openWithRedirect: () => {}, notifyAgent: () => {}, pendingDownload: null, pendingRedirect: null, clearPendingDownload: () => {}, clearPendingRedirect: () => {}, markSubmitted: () => {} });
const useModal = () => useContext(ModalCtx);


function Nav() {
  const [open, setOpen] = useState(false);
  const { open: openModal } = useModal();
  const { lang, setLang, t } = useLang();
  const links = [
    { label: t("nav.north"), href: "#north" },
    { label: t("nav.south"), href: "#south" },
    { label: t("nav.floorplans"), href: "#condos" },
    { label: t("nav.location"), href: "#location" },
    { label: t("nav.gallery"), href: "#gallery" },
  ];
  return (
    <header className="absolute top-0 left-0 right-0 z-40">
      <div className="bg-gold">
        <div className="mx-auto max-w-[1400px] px-6 py-2 md:px-12 text-center">
          <p className="eyebrow text-ink text-[10px] md:text-xs tracking-[0.2em]">
            {t("banner.construction")} <span className="mx-2 opacity-60">|</span> {t("banner.starting")} <span className="mx-2 opacity-60">|</span> {t("banner.sold")}
          </p>
        </div>
      </div>
      <div className="mx-auto max-w-[1400px] px-4 md:px-12 pt-3 md:pt-4 flex flex-wrap items-center justify-between text-white text-[10px] md:text-xs tracking-wide">
        <div className="flex items-center gap-1.5 md:gap-4">
          <a href="mailto:sales@luxurymiamiprojects.com" className="flex items-center gap-1 md:gap-2 hover:text-gold transition-colors">
            <svg className="w-3 h-3 md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="m3 7 9 6 9-6"/></svg>
            <span className="truncate max-w-[140px] md:max-w-none">sales@luxurymiamiprojects.com</span>
          </a>
          <span className="hidden md:inline opacity-60">|</span>
          <a href="tel:+17866571952" className="flex items-center gap-1 md:gap-2 hover:text-gold transition-colors">
            <svg className="w-3 h-3 md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72A2 2 0 0 1 22 16.92z"/></svg>
            (786) 657-1952
          </a>
        </div>
        <div className="flex items-center gap-1 md:gap-2">
          <button
            type="button"
            onClick={() => setLang("en")}
            className={`cursor-pointer transition-colors ${lang === "en" ? "text-gold" : "hover:text-gold"}`}
          >
            EN
          </button>
          <span className="opacity-60">|</span>
          <button
            type="button"
            onClick={() => setLang("es")}
            className={`cursor-pointer transition-colors ${lang === "es" ? "text-gold" : "hover:text-gold"}`}
          >
            ES
          </button>
        </div>
      </div>
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-6 md:px-12 md:py-8">
        <a href="#home" className="text-white">
          <img src={logoAsset.url} alt="The Residences at Mandarin Oriental Miami" width={221} height={147} fetchPriority="high" decoding="async" className="h-[5.25rem] w-auto md:h-24" />
        </a>
        <nav className="hidden items-center gap-10 lg:flex absolute left-1/2 -translate-x-1/2">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="eyebrow text-white/85 transition-colors hover:text-gold"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <button onClick={openModal} className="btn-gold hidden lg:inline-flex">
          {t("nav.request")}
        </button>
        <button
          aria-label="Menu"
          onClick={() => setOpen(true)}
          className={`lg:hidden text-white relative ${open ? "hidden" : ""}`}
        >
          <div className="space-y-1.5">
            <span className="block h-px w-7 bg-white" />
            <span className="block h-px w-7 bg-white" />
            <span className="block h-px w-5 bg-white" />
          </div>
        </button>
      </div>
      <div
        className={`lg:hidden fixed inset-0 z-50 bg-ink transition-transform duration-500 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!open}
      >
        <button
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className="absolute top-6 right-6 text-white text-3xl leading-none"
        >
          ×
        </button>
        <div className="flex h-full flex-col justify-center gap-8 px-10">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                const id = l.href.replace("#", "");
                setTimeout(() => {
                  const el = document.getElementById(id);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }, 550);
              }}
              className="eyebrow text-white/90 text-lg"
            >
              {l.label}
            </a>
          ))}
          <button
            onClick={() => {
              setOpen(false);
              setTimeout(() => openModal(), 550);
            }}
            className="btn-gold mt-4 self-start"
          >
            {t("nav.request")}
          </button>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  const { open: openModal } = useModal();
  const { t } = useLang();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [needsTap, setNeedsTap] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  const tryPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    if (!v.paused) return;
    const p = v.play();
    if (p && typeof p.then === "function") {
      p.then(() => setNeedsTap(false)).catch(() => setNeedsTap(true));
    }
  };

  useEffect(() => {
    // Defer loading the (large) hero video until after the page is idle,
    // and skip entirely on save-data / very slow connections. This prevents
    // Lighthouse from recording an aborted request as a console error.
    const conn = (navigator as unknown as { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    if (conn?.saveData || conn?.effectiveType === "slow-2g" || conn?.effectiveType === "2g") {
      return;
    }
    const attach = () => setVideoSrc(mandarinHeroVideoAsset.url);
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    let t: ReturnType<typeof setTimeout> | undefined;
    if (idle) {
      idle(attach, { timeout: 3000 });
    } else {
      t = setTimeout(attach, 2000);
    }
    return () => { if (t) clearTimeout(t); };
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoSrc) return;
    tryPlay();
    const onReady = () => tryPlay();
    v.addEventListener("loadedmetadata", onReady);
    v.addEventListener("canplay", onReady);
    v.addEventListener("loadeddata", onReady);
    const onAny = () => { tryPlay(); };
    document.addEventListener("touchstart", onAny, { passive: true, once: true });
    document.addEventListener("touchend", onAny, { passive: true, once: true });
    document.addEventListener("click", onAny, { once: true });
    document.addEventListener("visibilitychange", onAny);
    window.addEventListener("focus", onAny);
    window.addEventListener("pageshow", onAny);
    return () => {
      v.removeEventListener("loadedmetadata", onReady);
      v.removeEventListener("canplay", onReady);
      v.removeEventListener("loadeddata", onReady);
      document.removeEventListener("touchstart", onAny);
      document.removeEventListener("touchend", onAny);
      document.removeEventListener("click", onAny);
      document.removeEventListener("visibilitychange", onAny);
      window.removeEventListener("focus", onAny);
      window.removeEventListener("pageshow", onAny);
    };
  }, [videoSrc]);



  return (
    <section id="home" className="relative h-screen-safe w-full overflow-hidden bg-black">
      <video
        ref={videoRef}
        {...(videoSrc ? { src: videoSrc } : {})}
        poster={heroImg}
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        {...({ "webkit-playsinline": "true", "x5-playsinline": "true" } as Record<string, string>)}

        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
        onClick={tryPlay}
        onCanPlay={tryPlay}
        onLoadedData={tryPlay}
        onTimeUpdate={(e) => {
          const v = e.currentTarget;
          if (v.currentTime >= 162) {
            v.currentTime = 0;
          }
        }}
        className="absolute inset-0 z-0 h-full w-full object-cover"
      />


      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/20 to-black/70 pointer-events-none" />

      <Nav />

      <div className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center text-white">
        <div className="eyebrow text-gold mt-28 md:mt-36">{t("hero.eyebrow")}</div>

        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <button onClick={openModal} className="btn-outline-light">{t("hero.requestPriceList")}</button>
          <button onClick={openModal} className="btn-gold">{t("hero.schedule")}</button>
        </div>
      </div>




      <button
        onClick={() => document.getElementById('intro')?.scrollIntoView({ behavior: 'smooth' })}
        className="eyebrow absolute bottom-10 md:bottom-8 left-1/2 z-10 -translate-x-1/2 flex flex-col items-center gap-2 text-white animate-bounce hover:text-gold transition-colors cursor-pointer"
      >
        <span className="tracking-[0.3em] text-xs">{t("hero.scroll")}</span>
        <span className="text-lg">↓</span>
      </button>
    </section>
  );
}


function Intro() {
  const { t } = useLang();
  return (
    <>
      <div className="h-20 bg-background" />
      <section id="intro" className="bg-background px-6 py-24 md:py-32">
      <div className="mx-auto max-w-3xl text-center">
        <div className="eyebrow text-gold">{t("intro.eyebrow")}</div>
        <h2 className="mt-6 font-serif text-3xl leading-tight md:text-5xl">
          {t("intro.title")}
        </h2>
        <div className="mx-auto mt-10 h-px w-20 bg-gold" />
        <p className="mt-10 text-base font-light leading-relaxed text-muted-foreground md:text-lg">
          {t("intro.body")}
        </p>
      </div>
    </section>
    </>
  );
}

type TowerProps = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  image: string;
  reverse?: boolean;
  hideImageMobile?: boolean;
  stats: { value: string; label: string }[];
  downloadUrl?: string;
};

function Tower({ id, eyebrow, title, body, image, reverse, hideImageMobile, stats, downloadUrl }: TowerProps) {
  const { openWithDownload } = useModal();
  const { t } = useLang();
  return (
    <section id={id} className="bg-background md:px-8 lg:px-12 2xl:px-0">
      <div
        className={`mx-auto grid max-w-[1400px] grid-cols-1 items-center gap-0 md:grid-cols-2 ${
          reverse ? "md:[&>:first-child]:order-2" : ""
        }`}
      >
        <div className={`relative overflow-hidden bg-black group ${hideImageMobile ? "hidden md:block" : ""}`}>
          <img
            src={image}
            alt={title}
            width={1600}
            height={1100}
            className="h-[60vh] w-full object-cover md:h-[70vh] 2xl:h-[80vh] transition-transform duration-[2000ms] ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-1000 pointer-events-none" />
          <div className="absolute inset-6 border border-white/0 group-hover:border-white/40 transition-all duration-1000 pointer-events-none scale-105 group-hover:scale-100" />
        </div>

        <div className="px-6 py-16 md:px-10 md:py-16 lg:px-14 lg:py-20 2xl:px-20 2xl:py-36">
          <div className="eyebrow text-gold">{eyebrow}</div>
          <h3 className="mt-6 font-serif text-3xl leading-tight md:text-4xl 2xl:text-5xl">{title}</h3>
          <div className="mt-8 h-px w-16 bg-gold" />
          <p className="mt-8 text-base font-light leading-relaxed text-muted-foreground">{body}</p>
          <button
            type="button"
            onClick={() => openWithDownload(downloadUrl ?? floorPlansAsset.url)}
            className="mt-8 inline-flex items-center gap-2 border border-gold px-6 py-3 text-sm font-medium tracking-[0.15em] text-gold transition hover:bg-gold hover:text-white w-full justify-center sm:w-auto sm:justify-start"
          >
            <Home className="h-4 w-4" strokeWidth={1.5} />
            {t("downloads.floorPlans")}
          </button>
          <div className={`mt-10 grid gap-6 border-t border-border pt-8 text-center md:text-left ${stats.length === 3 ? "grid-cols-1 md:grid-cols-3" : "grid-cols-2"}`}>
            {stats.map((stat, idx) => (
              <div key={idx}>
                <div className="font-serif text-3xl text-gold">{stat.value}</div>
                <div className="eyebrow mt-2 text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}


function Amenities() {
  return (
    <section
      id="amenities"
      aria-label="Amenities at the residences"
      className="hidden md:block h-screen w-full bg-center bg-cover md:bg-fixed"
      style={{ backgroundImage: `url(${poolMandarinAsset.url})` }}
    />
  );
}


function Downloads() {
  const { open: openModal, openWithDownload, openWithRedirect } = useModal();
  const { t } = useLang();
  const items = [
    { label: t("downloads.brochure"), Icon: BookOpen, download: brochureAsset.url as string | null, redirect: null as string | null },
    { label: t("downloads.factSheet"), Icon: FileText, download: factsheetAsset.url as string | null, redirect: null as string | null },
    { label: t("downloads.floorPlans"), Icon: Home, download: floorPlansAsset.url as string | null, redirect: null as string | null },
    { label: t("downloads.renderings"), Icon: ImageIcon, download: null as string | null, redirect: "/renderings" as string | null },
  ];
  return (
    <section className="bg-background px-6 py-24 md:py-32">
      <div className="mx-auto max-w-5xl text-center">
        <div className="eyebrow text-foreground/70">{t("downloads.eyebrow")}</div>
        <h3 className="mt-4 font-serif text-4xl leading-tight text-gold md:text-5xl">
          <span className="block md:inline">{t("downloads.title1")}</span>{" "}
          <span className="block md:inline">{t("downloads.title2")}</span>
        </h3>
        <div className="mx-auto mt-8 h-px w-16 bg-gold" />
        <div className="mt-14 grid grid-cols-2 gap-8 md:grid-cols-4 md:gap-12">
          {items.map(({ label, Icon, download, redirect }) => (
            <button
              key={label}
              onClick={() => (redirect ? openWithRedirect(redirect) : download ? openWithDownload(download) : openModal())}
              className="group flex flex-col items-center gap-5 transition"
            >
              <span className="flex h-20 w-20 items-center justify-center rounded-full border border-gold/40 text-gold transition group-hover:bg-gold group-hover:text-white">
                <Icon className="h-8 w-8" strokeWidth={1.5} />
              </span>
              <span className="font-serif text-xl text-foreground transition group-hover:text-gold">
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function Location() {
  const { t } = useLang();
  const sectionRef = useRef<HTMLElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    const section = sectionRef.current;
    const img = imgRef.current;
    if (!section || !img) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let raf: number | null = null;
    let ticking = false;

    const update = () => {
      const rect = section.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const progress = (windowHeight - rect.top) / (windowHeight + rect.height);
      const clamped = Math.max(0, Math.min(1, progress));
      const offset = (clamped - 0.5) * 20;
      img.style.transform = `translate3d(0, ${offset}%, 0)`;
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      raf = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [isMobile]);

  return (
    <section
      id="location"
      ref={sectionRef}
      className="relative h-screen-safe w-full overflow-hidden"
    >
      {isMobile ? (
        <img
          ref={imgRef}
          src={locationAsset.url}
          alt="Mandarin Oriental Miami Location"
          className="absolute -top-[10%] left-0 h-[120%] w-full object-cover will-change-transform"
        />
      ) : (
        <div
          className="absolute inset-0 bg-center bg-cover bg-fixed"
          style={{ backgroundImage: `url(${locationAsset.url})` }}
        />
      )}
      <div className="absolute inset-0 bg-black/50" />
      <div className="absolute inset-0 flex items-center justify-center px-6 text-center text-white">
        <div className="max-w-2xl">
          <div className="eyebrow text-gold">{t("location.eyebrow")}</div>
          <h3 className="mt-6 font-serif text-3xl leading-tight md:text-5xl">
            {t("location.title")}
          </h3>
          <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/85">
            {t("location.body")}
          </p>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const { open: openModal } = useModal();
  const { t } = useLang();
  return (
    <section id="contact" className="bg-background px-6 pt-10 md:pt-14 pb-20 md:pb-24">
      <div className="mx-auto max-w-2xl text-center">
        <div className="eyebrow text-gold">{t("contact.eyebrow")}</div>
        <h3 className="mt-6 font-serif text-4xl leading-tight md:text-5xl">
          {t("contact.title")}
        </h3>
        <p className="mx-auto mt-6 max-w-md text-sm font-light leading-relaxed text-muted-foreground">
          {t("contact.body")}
        </p>
        <div className="mt-10 flex justify-center">
          <button onClick={openModal} className="btn-gold">{t("nav.request")}</button>
        </div>
      </div>
    </section>
  );
}

function RequestModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [agree, setAgree] = useState(true);
  const { notifyAgent, pendingDownload, pendingRedirect, clearPendingDownload, clearPendingRedirect, markSubmitted } = useModal();
  const navigate = useNavigate();
  const { t } = useLang();
  const [role, setRole] = useState("");
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [roleError, setRoleError] = useState(false);
  const [timeframe, setTimeframe] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");



  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/70 backdrop-blur-sm md:items-start md:px-4 md:py-10"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full min-h-full bg-background shadow-2xl md:h-auto md:min-h-0 md:max-w-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-gold text-white shadow-lg transition hover:opacity-90 md:-top-3 md:-right-3"
        >
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="flex min-h-full flex-col justify-center px-8 py-16 text-center bg-background">
            <div className="eyebrow text-gold">{t("modal.thankYou")}</div>
            <p className="mt-4 font-serif text-2xl">{t("modal.thankYouBody")}</p>
            <button onClick={onClose} className="btn-gold mt-8">{t("modal.close")}</button>
          </div>
        ) : (
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!role) {
                setRoleError(true);
                return;
              }
              if (isSending) return;
              const formEl = e.currentTarget;
              const fd = new FormData(formEl);
              const payload = {
                form_location: "popup" as const,
                role: (fd.get("role") as string) || role,
                first_name: (fd.get("first") as string) || "",
                last_name: (fd.get("last") as string) || "",
                email: (fd.get("email") as string) || "",
                phone: (fd.get("phone") as string) || "",
                timeframe: (fd.get("timeframe") as string) || "",
                comments: (fd.get("comments") as string) || "",
              };
              setIsSending(true);
              setSendError("");
              try {
                const res = await fetch("/api/public/send-lead-to-fub", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(payload),
                });
                const json = await res.json().catch(() => ({}));
                if (!res.ok || !json.ok) {
                  setSendError(t("modal.sendError") || "Something went wrong. Please try again.");
                  setIsSending(false);
                  return;
                }
              } catch {
                setSendError(t("modal.sendError") || "Something went wrong. Please try again.");
                setIsSending(false);
                return;
              }
              setIsSending(false);
              markSubmitted();
              (window as unknown as { dataLayer?: unknown[] }).dataLayer =
                (window as unknown as { dataLayer?: unknown[] }).dataLayer || [];
              (window as unknown as { dataLayer: unknown[] }).dataLayer.push({ event: "lead_form_luxury", ...payload });
              if (pendingDownload) {
                const a = document.createElement("a");
                a.href = pendingDownload;
                a.download = "";
                a.rel = "noopener";
                document.body.appendChild(a);
                a.click();
                a.remove();
                clearPendingDownload();
              }
              if (pendingRedirect) {
                const target = pendingRedirect;
                clearPendingRedirect();
                onClose();
                navigate({ to: target });
                return;
              }
              setSubmitted(true);
            }}

            className="flex min-h-full flex-col px-6 py-10 bg-background md:px-10"
          >
            <div className="text-center">
              <h3 className="font-serif text-3xl text-gold">{t("modal.title")}</h3>
              <p className="mt-3 text-sm font-light text-muted-foreground">
                {t("modal.subtitle")}
              </p>
            </div>

            <div className="mt-8 space-y-4">
              <div className="relative">
                <input
                  type="hidden"
                  name="role"
                  value={role}
                />
                <button
                  type="button"
                  aria-label={t("modal.rolePlaceholder")}
                  aria-expanded={roleMenuOpen}
                  aria-invalid={roleError}
                  onClick={() => setRoleMenuOpen((open) => !open)}
                  className={`relative w-full border bg-transparent px-4 py-3 pr-10 text-left text-base font-light outline-none transition ${roleError ? "border-gold" : "border-border focus:border-gold"}`}
                >
                  <span className="text-sm font-light text-muted-foreground/70 md:text-base">
                    {role ? (role === "buyer" ? t("modal.buyer") : t("modal.agent")) : t("modal.rolePlaceholder")}
                  </span>

                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                </button>
                {roleMenuOpen && (
                  <div className="absolute left-0 right-0 top-full z-20 border border-border bg-background shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        setRole("buyer");
                        setRoleError(false);
                        setRoleMenuOpen(false);
                      }}
                      className="block w-full px-4 py-3 text-left text-base font-light text-foreground transition hover:bg-muted"
                    >
                      {t("modal.buyer")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        notifyAgent();
                        setRole("");
                        setRoleError(false);
                        setRoleMenuOpen(false);
                      }}
                      className="block w-full px-4 py-3 text-left text-base font-light text-foreground transition hover:bg-muted"
                    >
                      {t("modal.agent")}
                    </button>
                  </div>
                )}
              </div>
              <ModalField name="first" placeholder={t("modal.firstName")} />
              <ModalField name="last" placeholder={t("modal.lastName")} />
              <ModalField name="email" type="email" placeholder={t("modal.email")} />
              <PhoneField name="phone" placeholder={t("modal.phone")} variant="dark" />
              <div className="relative">
                <select
                  name="timeframe"
                  aria-label={t("modal.timeframe.placeholder")}
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className={`w-full appearance-none border border-border bg-transparent px-4 py-3 pr-10 font-light outline-none transition focus:border-gold ${timeframe ? "text-foreground" : "text-muted-foreground/70"}`}
                >
                  <option value="" disabled className="bg-background text-foreground">{t("modal.timeframe.placeholder")}</option>
                  <option value="1m" className="bg-background text-foreground">{t("modal.timeframe.1")}</option>
                  <option value="2-3m" className="bg-background text-foreground">{t("modal.timeframe.2")}</option>
                  <option value="3-6m" className="bg-background text-foreground">{t("modal.timeframe.3")}</option>
                  <option value="6m+" className="bg-background text-foreground">{t("modal.timeframe.4")}</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>


              <textarea
                rows={4}
                name="comments"
                placeholder={t("modal.comments")}
                className="w-full border border-border bg-transparent px-4 py-3 font-light text-foreground outline-none transition focus:border-gold"
              />
            </div>

            <label className="mt-6 flex items-start gap-3 text-xs font-light leading-relaxed text-muted-foreground">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-0.5 h-4 w-4 flex-shrink-0 accent-[var(--gold)]"
              />
              <span>{t("modal.consent")}</span>
            </label>

            <p className="mt-6 text-xs text-muted-foreground">{t("modal.required")}</p>

            {sendError && (
              <p className="mt-3 text-sm text-gold" role="alert">{sendError}</p>
            )}

            <button
              type="submit"
              disabled={!agree || isSending}
              className="btn-gold mt-4 w-full justify-center disabled:opacity-50"
            >
              {isSending ? "Sending…" : t("modal.submit")}
            </button>
          </form>
        )}
      </div>

    </div>
  );
}

function AgentNotice({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { t } = useLang();
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-background px-8 py-10 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          onClick={onClose}
          className="absolute -top-3 -right-3 flex h-10 w-10 items-center justify-center rounded-full bg-gold text-white shadow-lg transition hover:opacity-90"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="eyebrow text-gold">{t("agent.notice")}</div>
        <h3 className="mt-3 font-serif text-2xl text-gold">{t("agent.title")}</h3>
        <p className="mt-4 text-sm font-light leading-relaxed text-muted-foreground">
          {t("agent.body1")}
          <a href="tel:+17866571952" className="font-medium text-gold">
            786-657-1952
          </a>
          .
        </p>
        <button onClick={onClose} className="btn-gold mt-8">
          {t("agent.close")}
        </button>
      </div>
    </div>
  );
}

function DownloadConfirmModal({ isOpen, label, onClose, onConfirm }: { isOpen: boolean; label: string; onClose: () => void; onConfirm: () => void }) {
  const { t } = useLang();
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-background px-8 py-10 text-center shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          aria-label="Close"
          onClick={onClose}
          className="absolute -top-3 -right-3 flex h-10 w-10 items-center justify-center rounded-full bg-gold text-white shadow-lg transition hover:opacity-90"
        >
          <X className="h-5 w-5" />
        </button>
        <div className="eyebrow text-gold">{t("modal.thankYou")}</div>
        <h3 className="mt-3 font-serif text-2xl text-gold">{label}</h3>
        <button onClick={onConfirm} className="btn-gold mt-8 w-full justify-center">
          {label}
        </button>
      </div>
    </div>
  );
}



function ModalField({
  name,
  type = "text",
  placeholder,
}: {
  name: string;
  type?: string;
  placeholder: string;
}) {
  return (
    <input
      id={name}
      name={name}
      type={type}
      required
      placeholder={placeholder}
      className="w-full border border-border bg-transparent px-4 py-3 font-light text-foreground outline-none transition placeholder:text-muted-foreground/70 focus:border-gold"
    />
  );
}

function VideoBanners() {
  const banners: { poster: string; label: string; src?: string }[] = [
    { poster: collectionIIResidence03PosterAsset.url, label: "Collection II · Residence 03", src: collectionIIResidence03VideoAsset.url },
    { poster: residence02PosterAsset.url, label: "Collection I · Residence 02", src: residence02VideoAsset.url },
    { poster: swireMakingNeighborhoodPosterAsset.url, label: "Swire Properties · Making a Neighborhood", src: swireMakingNeighborhoodVideoAsset.url },
  ];
  const [current, setCurrent] = useState(0);
  const [openSrc, setOpenSrc] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const touchStartX = useRef<number | null>(null);
  const { open: openModal, openWithDownload } = useModal();

  const noOffsetSrc = banners[2].src;

  const openVideo = (src: string) => {
    setOpenSrc(src);
  };

  const closeVideo = () => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
    }
    setOpenSrc(null);
  };

  const goNext = () => setCurrent((prev) => (prev === banners.length - 1 ? 0 : prev + 1));
  const goPrev = () => setCurrent((prev) => (prev === 0 ? banners.length - 1 : prev - 1));

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const diff = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(diff) < 50) return;
    if (diff < 0) goNext();
    else goPrev();
  };

  useEffect(() => {
    if (!openSrc) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeVideo();
    document.addEventListener("keydown", onKey);
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [openSrc]);

  useEffect(() => {
    if (!openSrc) return;
    const video = videoRef.current;
    if (!video) return;
    video.src = openSrc;
    try {
      video.currentTime = openSrc === noOffsetSrc ? 0 : 3;
    } catch {}
    const p = video.play();
    if (p) p.catch(() => {});
  }, [openSrc, noOffsetSrc]);

  return (
    <section className="bg-background pt-8 md:pt-10 pb-10 md:pb-14">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12">
        {/* Desktop: 3 in a row, middle has play icon */}
        <div className="hidden md:grid grid-cols-3 gap-4">
          {banners.map((b, i) => (
            <div
              key={i}
              className={`relative aspect-[4/5] overflow-hidden bg-black group ${b.src ? "cursor-pointer" : ""}`}
              onClick={() => b.src && openVideo(b.src)}
            >
              <img src={b.poster} alt={b.label} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {b.src && (
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-white/90 shadow-xl">
                    <Play className="h-8 w-8 fill-ink text-ink translate-x-0.5" />
                  </span>
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Mobile: single item carousel with play icon and dots */}
        <div className="md:hidden">
          <div
            className={`relative aspect-[4/5] overflow-hidden bg-black ${banners[current].src ? "cursor-pointer" : ""}`}
            onClick={() => banners[current].src && openVideo(banners[current].src!)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            <img src={banners[current].poster} alt={banners[current].label} className="h-full w-full object-cover" />
            {banners[current].src && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/90 shadow-xl">
                  <Play className="h-7 w-7 fill-ink text-ink translate-x-0.5" />
                </span>
              </span>
            )}
          </div>
          <div className="mt-4 flex items-center justify-center gap-2">
            {banners.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setCurrent(i)}
                aria-label={`Go to video ${i + 1}`}
                className="flex h-11 w-11 items-center justify-center"
              >
                <span
                  className={`block h-2 w-2 rounded-full transition-colors ${i === current ? "bg-gold" : "bg-foreground/25"}`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 md:mt-10 flex justify-center">
        <button onClick={() => openWithDownload(brochureAsset.url)} className="btn-gold">
          Download Brochure
        </button>
      </div>

      {openSrc &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 md:p-10 transition-opacity duration-300 opacity-100 pointer-events-auto"
            onClick={() => closeVideo()}
          >
            <button
              aria-label="Close video"
              onClick={() => closeVideo()}
              className="absolute right-4 top-4 md:right-6 md:top-6 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <video
              ref={videoRef}
              controls
              playsInline
              onClick={(e) => e.stopPropagation()}
              onLoadedMetadata={() => {
                const video = videoRef.current;
                if (video && openSrc !== noOffsetSrc && video.currentTime < 3) {
                  video.currentTime = 3;
                }
              }}
              className="max-h-full max-w-full"
            />
          </div>,
          document.body,
        )}
    </section>
  );
}

function Footer() {
  const [submitted, setSubmitted] = useState(false);
  const { notifyAgent } = useModal();
  const { t } = useLang();

  const [agree, setAgree] = useState(true);
  const [role, setRole] = useState("");
  const [roleMenuOpen, setRoleMenuOpen] = useState(false);
  const [roleError, setRoleError] = useState(false);
  const [timeframe, setTimeframe] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState("");



  return (
    <footer id="footer-contact" className="bg-ink text-white/70">
      <div className="mx-auto max-w-[1400px] px-6 py-20 md:px-12 md:py-24">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
          {/* Left: brand + info */}
          <div>
            <div className="eyebrow text-gold">{t("footer.eyebrow")}</div>
            <h3 className="mt-4 font-serif text-3xl leading-tight text-white md:text-5xl">
              {t("footer.title")}
            </h3>
            <p className="mt-6 max-w-md text-sm font-light leading-relaxed text-white/70">
              {t("footer.body")}
            </p>

            <div className="mt-10">
              <div className="eyebrow text-white">{t("footer.deposit")}</div>
              <ul className="mt-4 space-y-2 text-sm font-light text-white/80">
                <li className="flex items-center gap-3"><span className="h-1 w-1 rounded-full bg-gold" /> {t("footer.deposit1")}</li>
                <li className="flex items-center gap-3"><span className="h-1 w-1 rounded-full bg-gold" /> {t("footer.deposit2")}</li>
                <li className="flex items-center gap-3"><span className="h-1 w-1 rounded-full bg-gold" /> {t("footer.deposit3")}</li>
                <li className="flex items-center gap-3"><span className="h-1 w-1 rounded-full bg-gold" /> {t("footer.deposit4")}</li>
              </ul>
            </div>

            <div className="mt-10 md:max-w-md">
              <div className="eyebrow text-white">{t("footer.contactInfo")}</div>
              <p className="mt-3 text-sm font-light">786-657-1952</p>
              <p className="text-sm font-light">sales@luxurymiamiprojects.com</p>
            </div>
          </div>

          {/* Right: contact form */}
          <div className="relative">
            <div className="absolute -inset-px bg-gradient-to-br from-gold/30 via-transparent to-gold/10 blur-sm" aria-hidden />
            <div className="relative bg-white p-8 text-foreground shadow-2xl md:p-10">
              {submitted ? (
                <div className="py-10 text-center">
                  <div className="eyebrow text-gold">{t("modal.thankYou")}</div>
                  <p className="mt-4 font-serif text-2xl text-ink">{t("modal.thankYouBody")}</p>
                </div>
              ) : (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    if (!role) {
                      setRoleError(true);
                      return;
                    }
                    if (isSending) return;
                    const fd = new FormData(e.currentTarget);
                    const payload = {
                      form_location: "footer" as const,
                      role: (fd.get("role") as string) || role,
                      first_name: (fd.get("first") as string) || "",
                      last_name: (fd.get("last") as string) || "",
                      email: (fd.get("email") as string) || "",
                      phone: (fd.get("phone") as string) || "",
                      timeframe: (fd.get("timeframe") as string) || "",
                      comments: (fd.get("comments") as string) || "",
                    };
                    setIsSending(true);
                    setSendError("");
                    try {
                      const res = await fetch("/api/public/send-lead-to-fub", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                      });
                      const json = await res.json().catch(() => ({}));
                      if (!res.ok || !json.ok) {
                        setSendError("Something went wrong. Please try again.");
                        setIsSending(false);
                        return;
                      }
                    } catch {
                      setSendError("Something went wrong. Please try again.");
                      setIsSending(false);
                      return;
                    }
                    setIsSending(false);
                    setSubmitted(true);
                    (window as unknown as { dataLayer?: unknown[] }).dataLayer =
                      (window as unknown as { dataLayer?: unknown[] }).dataLayer || [];
                    (window as unknown as { dataLayer: unknown[] }).dataLayer.push({ event: "lead_form_luxury", ...payload });
                  }}
                  className="space-y-4"
                >
                  <div className="relative">
                    <input
                      type="hidden"
                      name="role"
                      value={role}
                    />
                    <button
                      type="button"
                      aria-label={t("footer.rolePlaceholder")}
                      aria-expanded={roleMenuOpen}
                      aria-invalid={roleError}
                      onClick={() => setRoleMenuOpen((open) => !open)}
                      className={`relative w-full border bg-white px-4 py-3 pr-10 text-left text-base font-light outline-none transition ${roleError ? "border-gold" : "border-black/15 focus:border-gold"}`}
                    >
                      <span className="text-sm font-light text-black/40 md:text-base">
                        {role ? (role === "buyer" ? t("modal.buyer") : t("footer.agent")) : t("footer.rolePlaceholder")}
                      </span>

                      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/60" />
                    </button>
                    {roleMenuOpen && (
                      <div className="absolute left-0 right-0 top-full z-20 border border-black/15 bg-white shadow-lg">
                        <button
                          type="button"
                          onClick={() => {
                            setRole("buyer");
                            setRoleError(false);
                            setRoleMenuOpen(false);
                          }}
                          className="block w-full px-4 py-3 text-left text-base font-light text-ink transition hover:bg-muted"
                        >
                          {t("modal.buyer")}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                          notifyAgent();
                            setRole("");
                            setRoleError(false);
                            setRoleMenuOpen(false);
                          }}
                          className="block w-full px-4 py-3 text-left text-base font-light text-ink transition hover:bg-muted"
                        >
                          {t("footer.agent")}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FooterField name="first" placeholder={t("modal.firstName")} />
                    <FooterField name="last" placeholder={t("modal.lastName")} />
                  </div>
                  <FooterField name="email" type="email" placeholder={t("modal.email")} />
                  <PhoneField name="phone" placeholder={t("modal.phone")} idPrefix="footer" variant="light" />
                  <div className="relative">
                    <select
                      name="timeframe"
                      aria-label={t("modal.timeframe.placeholder")}
                      value={timeframe}
                      onChange={(e) => setTimeframe(e.target.value)}
                      className={`w-full appearance-none border border-black/15 bg-white px-4 py-3 pr-10 font-light outline-none transition focus:border-gold ${timeframe ? "text-ink" : "text-black/40"}`}
                    >
                      <option value="" disabled>{t("modal.timeframe.placeholder")}</option>
                      <option value="1m">{t("modal.timeframe.1")}</option>
                      <option value="2-3m">{t("modal.timeframe.2")}</option>
                      <option value="3-6m">{t("modal.timeframe.3")}</option>
                      <option value="6m+">{t("modal.timeframe.4")}</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/60" />
                  </div>


                  <textarea
                    rows={4}
                    name="comments"
                    placeholder={t("footer.commentsPlaceholder")}
                    className="w-full border border-black/15 bg-white px-4 py-3 font-light text-ink outline-none transition placeholder:text-black/40 focus:border-gold"
                  />

                  <label className="flex items-start gap-3 pt-2 text-xs font-light leading-relaxed text-black/60">
                    <input
                      type="checkbox"
                      checked={agree}
                      onChange={(e) => setAgree(e.target.checked)}
                      className="mt-0.5 h-4 w-4 flex-shrink-0 accent-black"
                    />

                    <span>{t("footer.consent")}</span>
                  </label>

                  <p className="text-xs text-black/40">{t("footer.required")}</p>

                  {sendError && (
                    <p className="text-sm text-gold" role="alert">{sendError}</p>
                  )}

                  <button
                    type="submit"
                    disabled={!agree || isSending}
                    className="mt-2 w-full bg-ink px-6 py-4 text-sm font-medium tracking-[0.2em] text-white transition hover:bg-gold disabled:opacity-50"
                  >
                    {isSending ? "Sending…" : t("footer.submit")}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>

      <div className="border-t border-white/10 bg-white text-ink">
        <div className="mx-auto max-w-[1400px] px-6 py-8 md:px-12">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="flex flex-col items-start gap-4">
              <img
                src={luxuryIntlAsset.url}
                alt="Luxury International Realty"
                className="h-14 w-auto object-contain"
              />
              <p className="text-sm text-ink">
                Please mail to- 2665 S. Bayshore Dr, Suite 220, Coconut Grove, FL 33133
              </p>
              <img
                src={zagaTeamAsset.url}
                alt="Zaga Team - Luxury Real Estate"
                className="h-20 w-auto object-contain"
              />
              <p className="text-sm text-ink md:hidden">(786) 998-4128</p>
              <img
                src={realtorMlsAsset.url}
                alt="Realtor, MLS and Equal Housing Opportunity"
                className="h-14 w-auto object-contain md:hidden"
              />
            </div>

            <div className="hidden items-end justify-end md:flex">
              <img
                src={realtorMlsAsset.url}
                alt="Realtor, MLS and Equal Housing Opportunity"
                className="h-14 w-auto object-contain"
              />
            </div>
          </div>

          <p className="mt-6 hidden text-sm text-ink md:block">(786) 998-4128</p>

          <p className="mt-6 text-xs leading-relaxed text-ink/70">
            {t("footer.legal")}
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterField({
  name,
  type = "text",
  placeholder,
}: {
  name: string;
  type?: string;
  placeholder: string;
}) {
  return (
    <input
      id={`footer-${name}`}
      name={name}
      type={type}
      required
      placeholder={placeholder}
      className="w-full border border-black/15 bg-white px-4 py-3 font-light text-ink outline-none transition placeholder:text-black/40 focus:border-gold"

    />
  );
}


function Landing() {
  return (
    <LangProvider>
      <LandingInner />
    </LangProvider>
  );
}

function LandingInner() {
  const [modalOpen, setModalOpen] = useState(false);
  const [agentNoticeOpen, setAgentNoticeOpen] = useState(false);
  const [contactCardOpen, setContactCardOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState<string | null>(null);
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [confirmData, setConfirmData] = useState<{ url?: string; path?: string } | null>(null);
  const navigate = useNavigate();
  const { t } = useLang();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.hash) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    window.scrollTo(0, 0);
    try {
      if (sessionStorage.getItem("mo_form_submitted") === "1") setHasSubmitted(true);
    } catch {}
  }, []);
  useEffect(() => {
    const timer = setTimeout(() => setContactCardOpen(true), 12000);
    return () => clearTimeout(timer);
  }, []);
  const triggerDownload = (url: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };
  const markSubmitted = () => {
    setHasSubmitted(true);
    try { sessionStorage.setItem("mo_form_submitted", "1"); } catch {}
  };
  const labelFor = (target: string) => {
    const s = target.toLowerCase();
    if (s.includes("brochure")) return t("downloads.brochure");
    if (s.includes("factsheet") || s.includes("fact-sheet") || s.includes("fact_sheet")) return t("downloads.factSheet");
    if (s.includes("floor")) return t("downloads.floorPlans");
    if (s.includes("rendering")) return t("downloads.renderings");
    return t("downloads.brochure");
  };
  return (
    <ModalCtx.Provider value={{
      open: () => { setPendingDownload(null); setPendingRedirect(null); setModalOpen(true); },
      openWithDownload: (url: string) => {
        if (hasSubmitted) { setConfirmData({ url }); return; }
        setPendingDownload(url); setPendingRedirect(null); setModalOpen(true);
      },
      openWithRedirect: (path: string) => {
        if (hasSubmitted) { setConfirmData({ path }); return; }
        setPendingRedirect(path); setPendingDownload(null); setModalOpen(true);
      },
      notifyAgent: () => setAgentNoticeOpen(true),
      pendingDownload,
      pendingRedirect,
      clearPendingDownload: () => setPendingDownload(null),
      clearPendingRedirect: () => setPendingRedirect(null),
      markSubmitted,
    }}>



      <main className="bg-background text-foreground">
        <Hero />
        <CondosForSale />
        <div id="intro" />
        <div id="towers">
          <Reveal direction="right">
            <Tower
              id="north"
              eyebrow={t("north.eyebrow")}
              title={t("north.title")}
              body={t("north.body")}
              image={northTowerImg}
              stats={[
                { value: "33", label: t("north.stat.stories") },
                { value: "70", label: t("north.stat.residences") },
                { value: "$4M", label: t("north.stat.starting") },
              ]}
            />
          </Reveal>
          <Reveal direction="left">
            <Tower
              id="south"
              eyebrow={t("south.eyebrow")}
              title={t("south.title")}
              body={t("south.body")}
              image={interiorImg}
              reverse
              stats={[
                { value: "66", label: t("south.stat.stories") },
                { value: "228", label: t("south.stat.residences") },
                { value: "$7M", label: t("south.stat.starting") },
              ]}
              downloadUrl={southFloorPlansAsset.url}
            />
          </Reveal>


          <div className="md:hidden">
            <ParallaxHero image={residencesParallaxAsset.url} />
          </div>
        </div>
        <Amenities />
        <Reveal direction="left"><Downloads /></Reveal>
        <Location />
        <Reveal direction="left"><Gallery /></Reveal>
        <Reveal direction="right"><ContactSection /></Reveal>
        <Reveal direction="left"><VideoBanners /></Reveal>
        <Footer />
        <div className={cn(
          "fixed bottom-6 left-1/2 z-40 w-[340px] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg bg-white p-5 pr-8 text-center shadow-2xl mb-[env(safe-area-inset-bottom,0px)] md:left-auto md:right-6 md:translate-x-0 md:mb-0",
          "transition-all duration-700 ease-out",
          contactCardOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
        )}>
          <button
            onClick={() => setContactCardOpen(false)}
            aria-label="Close"
            className="absolute right-2 top-2 text-foreground/60 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
          <p className="text-base font-medium text-foreground">{t("card.title")}</p>
          <p className="text-sm text-foreground/80">{t("card.body")}</p>
          <a href="tel:+17866571952" className="mt-3 flex items-center justify-center gap-2 text-lg font-medium text-gold">
            <Phone className="h-5 w-5" />
            786-657-1952
          </a>
          <p className="mt-3 text-xs text-foreground/60 leading-snug">
            {t("card.consent")}
          </p>
        </div>
        <RequestModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
        <AgentNotice isOpen={agentNoticeOpen} onClose={() => setAgentNoticeOpen(false)} />
        <DownloadConfirmModal
          isOpen={!!confirmData}
          label={confirmData ? labelFor(confirmData.url || confirmData.path || "") : ""}
          onClose={() => setConfirmData(null)}
          onConfirm={() => {
            if (!confirmData) return;
            if (confirmData.url) {
              triggerDownload(confirmData.url);
              setConfirmData(null);
            } else if (confirmData.path) {
              const target = confirmData.path;
              setConfirmData(null);
              navigate({ to: target });
            }
          }}
        />


      </main>
    </ModalCtx.Provider>
  );
}

